import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { Button, EnhancedAccordian } from '../../../../../ui';
import { AddQuestionModal } from '../../../../../ui/add_question_modal/add_question_modal';
import { PencilIcon, TrashIcon } from '../../../../../ui/icons/icons';
import { Modal } from '../../../../../ui/modal/modal';
import { addQuestionToAssessment } from '../../../../../utils/api_service/assessment_api';
import { addQuestion } from '../../../../../utils/api_service/job_api';
import {
  convertSecondsToTimeTaken,
  convertStringToUnix,
} from '../../../../../utils/helpers/time';
import {
  Question,
  QuestionInfoTotalCount,
  SubSkill,
} from '../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { SelectedQuestionContent } from './Containers/Components';
import SelectQuestions from './Containers/SelectQuestions';
import { useQuestions } from './Hooks';

interface IQuestions {
  assessmentId: string;
  skillList?: SelectOptions[];
  levelList?: SelectOptions[];
  jobTitleList?: SelectOptions[];
  subSkillList?: SubSkill[];
  setSkillList: (opts: SelectOptions[]) => void;
}

export const Questions: React.FC<IQuestions> = ({
  assessmentId,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
}) => {
  const {
    expandedIds,
    questionList,
    editQuestion,
    deleteQuestion,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    setQuestionList,
  } = useQuestions(assessmentId);

  const [editMap, setEditMap] = useState<Record<string, boolean>>({});
  const setDisabled = (id: string, disabled: boolean) =>
    setEditMap((m) => ({ ...m, [id]: disabled }));

  const questionTypes: SelectOptions[] = [
    { value: 'Modern', label: 'Video' },
    { value: 'Classic', label: 'Text' },
  ];
  const [totalCount, setTotalCount] = useState<QuestionInfoTotalCount>({
    totalPoints: 0,
    totalTime: '00:00',
  });
  const [addQuestionModalOpen, setAddQuestionModalOpen] =
    useState<boolean>(false);

  const handleChange = (question: Question) => {
    addQuestionToAssessment(
      questionList.length.toString(),
      question.id,
      assessmentId,
    ).then((response) => {
      const newQuestion = Object.assign(
        { assessmentQuestionId: response.id },
        question,
      );
      setQuestionList((ql) => [...ql, newQuestion]);
      const prevUnix = convertStringToUnix(totalCount.totalTime) || 0;
      const newUnix = prevUnix + question.time;
      setTotalCount({
        totalPoints: totalCount.totalPoints + question.points,
        totalTime: convertSecondsToTimeTaken(newUnix) || '00:00',
      });
    });
  };

  const addNewQuestion = (question: Question) => {
    addQuestion(question).then((response) => {
      handleChange(response);
      setAddQuestionModalOpen(false);
    });
  };
  const toggleAddQuestionModal = () => setAddQuestionModalOpen(true);

  return (
    <>
      { /* Header */ }
      <div className='flex justify-between items-center py-3'>
        <h2 className='text-xl font-medium text-primary-main'>Questions</h2>
        { questionList.length !== 0 && (
          <div className='flex gap-3'>
            <p>Total Score = { totalCount.totalPoints }</p>
            <p>Duration - { totalCount.totalTime } minutes</p>
            <p>{ questionList.length } questions added</p>
          </div>
        ) }
        <div className='flex gap-3'>
          <Button
              label={
                expandedIds.length === questionList.length
                  ? 'Collapse All'
                  : 'Expand All'
              }
              onClick={handleExpandAll}
              variant='secondary'
          />
          <Button
              label='Select Questions'
              onClick={handleOpenSelectQuestions}
              variant='secondary'
          />
          <Button
              label='Create New'
              onClick={toggleAddQuestionModal}
              variant='primary'
          />
        </div>
      </div>

      { /* Empty State */ }
      { questionList.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-20'>
          <svg
              className='w-16 h-16 text-gray-300 animate-pulse'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8 16v-4l-2-2m12 6v-4l2-2M14 4h2a2 2 0 012 2v3M6 4h2a2 2 0 012 2v3m0 6h8'
            />
          </svg>
          <p className='mt-4 text-gray-500 text-lg'>
            No questions selected yet
          </p>
        </div>
      ) : (
        /* Drag & Drop List */
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='questions'>
            { (prov) => (
              <div {...prov.droppableProps} ref={prov.innerRef}>
                { questionList.map((q, i) => {
                  const isDisabled = editMap[q.id] ?? true;
                  const onEditSave = (updated: Question) => {
                    setDisabled(q.id, true);
                    editQuestion(updated);
                  };
                  const returnSelectedJobTitles = () =>
                    (q.jobTitle ?? [])
                      .map((id) => {
                        const found = jobTitleList?.find((j) => j.value === id);
                        return found
                          ? { value: found.value, label: found.label }
                          : null;
                      })
                      .filter((x): x is SelectOptions => Boolean(x));

                  return (
                    <Draggable key={q.id} draggableId={q.id} index={i}>
                      { (p) => (
                        <div
                            ref={p.innerRef}
                            {...p.draggableProps}
                            {...p.dragHandleProps}
                            className='mb-2'
                        >
                          <EnhancedAccordian
                              title={q.question}
                              isExpanded={expandedIds.includes(q.id)}
                              onToggle={() => toggleExpand(q.id)}
                              rightIcons={[
                                <button
                                    key='edit'
                                    type='button'
                                    className='p-1 text-gray-500 hover:text-gray-700'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDisabled(q.id, false);
                                      if (!expandedIds.includes(q.id))
                                        toggleExpand(q.id);
                                    }}
                                >
                                  <PencilIcon className='w-4 h-4' />
                                </button>,
                                <button
                                    key='delete'
                                    type='button'
                                    className='p-1 text-gray-500 hover:text-red-500'
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      q.assessmentQuestionId &&
                                    deleteQuestion(q.assessmentQuestionId);
                                    }}
                                >
                                  <TrashIcon className='w-4 h-4' />
                                </button>,
                              ]}
                          >
                            <SelectedQuestionContent
                                toggledQuestion={q}
                                isDisabled={isDisabled}
                                setIsDisabled={(d) => setDisabled(q.id, d)}
                                editQuestion={onEditSave}
                                onAddNewSkill={() => {}}
                                returnSelectedJobTitles={returnSelectedJobTitles}
                                skills={skillList || []}
                                subSkills1={
                                  subSkillList
                                    ?.filter((s) => s.parentSkillId === q.skillId)
                                    .map((s) => ({
                                      value: s.id,
                                      label: s.subskillName,
                                    })) || []
                                }
                                subSkills2={
                                  subSkillList
                                    ?.filter(
                                      (s) => s.parentSkillId === q.subSkill1Id,
                                    )
                                    .map((s) => ({
                                      value: s.id,
                                      label: s.subskillName,
                                    })) || []
                                }
                                subSkills3={
                                  subSkillList
                                    ?.filter(
                                      (s) => s.parentSkillId === q.subSkill2Id,
                                    )
                                    .map((s) => ({
                                      value: s.id,
                                      label: s.subskillName,
                                    })) || []
                                }
                                levelList={levelList}
                                jobTitleList={jobTitleList || []}
                                questionTypes={questionTypes}
                            />
                          </EnhancedAccordian>
                        </div>
                      ) }
                    </Draggable>
                  );
                }) }
                { prov.placeholder }
              </div>
            ) }
          </Droppable>
        </DragDropContext>
      ) }

      { /* Select & Create Modals */ }
      <SelectQuestions
          isOpen={isOpenSelectQuestions}
          onCloseDrawer={handleCloseSelectQuestions}
          onSave={handleSaveSelection}
          onAddQuestion={handleAddQuestion}
          initialSelected={selectedQuestions}
          assessmentId={assessmentId}
          skillList={skillList}
          levelList={levelList}
          subSkillList={subSkillList}
          jobTitleList={jobTitleList}
      />

      <Modal
          visible={addQuestionModalOpen}
          modalContent={
            <AddQuestionModal
                addNewQuestion={addNewQuestion}
                skillList={skillList || []}
                levelList={levelList || []}
                jobTitleList={jobTitleList || []}
                subSkillList={subSkillList}
                setSkillList={setSkillList}
            />
          }
          toggle={() => setAddQuestionModalOpen(false)}
      />
    </>
  );
};
